import { useState, useEffect } from 'react';
import { Menu, Settings, MessageSquare, MoreHorizontal } from 'lucide-react';
import { SearchBar } from '../SearchBar';
import { CajaStatus } from '../CajaStatus';
import { CrearMenu } from '../CrearMenu';
import { UserMenu } from '../UserMenu';
import { EmpresaSelector } from '../EmpresaSelector';
import { AyudaGuiadaMenu } from '../AyudaGuiadaMenu';
import { Tooltip } from '../../components/Tooltip';
import { Button } from '../../components/Button';
import { useContextoTema } from '../../../contexts/ContextoTema';
import { useGlobalSearch } from '../../../hooks/useGlobalSearch';
import type { BuscarGlobalItemDto } from '../../../services/api/buscarGlobal.service';
import type { CajaData, CajaOpcion } from '../CajaStatus';
import type { DocumentoTipo } from '../CrearMenu';
import type { UserData, FacturacionElectronicaEstado } from '../UserMenu';
import type { PlanStatusCardProps } from '../UserMenu/PlanStatusCard.types';
import type { Empresa, Sede } from '../EmpresaSelector';
 
export interface TopBarProps {
  onToggleSidebar: () => void;
  showCaja?: boolean;
  empresas: Empresa[];
  sedes: Sede[];
  initialEmpresaId?: string;
  initialSedeId?: string;
  cajaData?: CajaData;
  cajasDisponibles?: CajaOpcion[];
  cajaSeleccionadaId?: string;
  onCambiarCaja?: (cajaId: string) => void;
  user: UserData;
  onChangeWorkspace?: (empresaId: string, sedeId: string) => void;
  onCrearDocumento?: (tipo: DocumentoTipo) => void;
  onCrearCliente?: () => void;
  onCrearProducto?: () => void;
  onVerMovimientosCaja?: () => void;
  onCerrarCaja?: () => void;
  onAbrirCaja?: () => void;
  onFeedback?: () => void;
  onAyudaGuiadaCambiar?: (activa: boolean) => void;
  tourModulo?: { id: string; nombre: string } | null;
  tourProgreso?: { pasoActual: number; totalPasos: number };
  onSettings?: () => void;
  settingsActive?: boolean;
  notificacionesSlot?: React.ReactNode;
  onLogout?: () => void;

  onLogoClick?: () => void;
  onAdministrarEmpresas?: () => void;
  facturacionElectronicaEstado?: FacturacionElectronicaEstado;
  onElectronicInvoicingClick?: () => void;
  onProfileClick?: () => void;
  onSearchNavigate?: (route: string, searchTerm?: string) => void;
  onHelpClick?: () => void;
  onOpenCommandPalette?: () => void;
  onSubscriptionsClick?: () => void;
  onShowKeyboardShortcuts?: () => void;
  planStatus?: PlanStatusCardProps;
}
 
export const TopBar = ({
  onToggleSidebar,
  showCaja = false,
  empresas,
  sedes,
  initialEmpresaId,
  initialSedeId,
  cajaData,
  cajasDisponibles,
  cajaSeleccionadaId,
  onCambiarCaja,
  user,
  onChangeWorkspace,
  onCrearDocumento,
  onCrearCliente,
  onCrearProducto,
  onVerMovimientosCaja,
  onCerrarCaja,
  onAbrirCaja,
  onFeedback,
  onAyudaGuiadaCambiar,
  tourModulo,
  tourProgreso,
  onSettings,
  settingsActive,
  notificacionesSlot,
  onLogout,
  onLogoClick,
  onAdministrarEmpresas,
  facturacionElectronicaEstado,
  onElectronicInvoicingClick,
  onProfileClick,
  onSearchNavigate,
  onHelpClick,
  onOpenCommandPalette,
  onSubscriptionsClick,
  onShowKeyboardShortcuts,
  planStatus,
}: TopBarProps) => {
  const { tema } = useContextoTema();
  const [empresaActualId, setEmpresaActualId] = useState(initialEmpresaId || empresas[0]?.id);
  const [sedeActualId, setSedeActualId] = useState(initialSedeId || sedes[0]?.id);
  const [masAbierto, setMasAbierto] = useState(false);
  const filaSecundaria = `${masAbierto ? 'flex' : 'hidden'} lg:flex lg:order-none`;

  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    sections: searchSections,
    isLoading: searchIsLoading,
    shouldSearch: searchShouldSearch,
    clear: clearSearch,
  } = useGlobalSearch();

  useEffect(() => {
    if (initialEmpresaId) setEmpresaActualId(initialEmpresaId);
  }, [initialEmpresaId]);

  useEffect(() => {
    if (initialSedeId) setSedeActualId(initialSedeId);
  }, [initialSedeId]);
  const handleVerMovimientos = () => {
    onVerMovimientosCaja?.();
  };
 
  const handleCerrarCaja = () => {
    onCerrarCaja?.();
  };
 
  const handleCrearDocumento = (tipo: DocumentoTipo) => {
    onCrearDocumento?.(tipo);
  };
 
  const handleCrearCliente = () => {
    onCrearCliente?.();
  };
 
  const handleCrearProducto = () => {
    onCrearProducto?.();
  };
 
  const handleChangeWorkspace = (empresaId: string, sedeId: string) => {
    setEmpresaActualId(empresaId);
    setSedeActualId(sedeId);
    onChangeWorkspace?.(empresaId, sedeId);
  };

  const handleSearchSelect = (type: string, item: unknown) => {
    const entity = item as BuscarGlobalItemDto;
    const tipo = entity?.tipo ?? type;

    const RUTAS: Record<string, string> = {
      comprobante:        '/comprobantes',
      cliente:            '/clientes',
      producto:           '/productos',
      cotizacion:         '/documentos-comerciales',
      orden_venta:        '/documentos-comerciales',
      nota_venta:         '/documentos-comerciales',
      orden_compra:       '/compras',
      requerimiento:      '/compras',
      comprobante_compra: '/compras',
      cobranza:           '/cobranzas',
    };
    const TERMINOS: Record<string, string | null | undefined> = {
      comprobante:        entity?.titulo,
      cliente:            entity?.subtitulo,
      producto:           entity?.subtitulo,
      cotizacion:         entity?.titulo,
      orden_venta:        entity?.titulo,
      nota_venta:         entity?.titulo,
      orden_compra:       entity?.titulo,
      requerimiento:      entity?.titulo,
      comprobante_compra: entity?.titulo,
      cobranza:           entity?.titulo,
    };

    const ruta = RUTAS[tipo];
    if (!ruta) return;

    onSearchNavigate?.(ruta, TERMINOS[tipo] ?? undefined);
    clearSearch();
  };

  const handleOpenCommandPalette = () => {
    onOpenCommandPalette?.();
  };

  return (
    <header className="bg-surface-0 border-b border-[color:var(--border-default)] flex flex-wrap lg:flex-nowrap items-center px-2 sm:px-4 py-2 lg:py-0 gap-2 lg:gap-4 lg:h-14 shrink-0">
      {/* Left Section */}
      <div className="flex items-center gap-2 lg:gap-4 order-1 lg:order-none">
        <Button
          iconOnly
          variant="ghost"
          size="md"
          onClick={onToggleSidebar}
          aria-label="Toggle sidebar"
          icon={<Menu className="w-5 h-5" />}
        />

        <div className="hidden lg:flex items-center gap-2">
          <img
            src="/Negocios%20Online%20-%20Logo.png"
            alt="Negocios Online"
            onClick={onLogoClick}
            className={`h-[38px] transition-opacity duration-150 ${tema === 'dark' ? 'brightness-0 invert' : ''} ${onLogoClick ? 'cursor-pointer hover:opacity-70' : ''}`}
          />
        </div>
      </div>
 
      {/* Center Section - Search */}
      <div className="contents lg:flex lg:items-center lg:gap-3 lg:flex-1 lg:ml-14">
        <div className="order-2 flex-1 min-w-0 lg:order-none lg:flex-none lg:w-full lg:max-w-[496px]">
          <SearchBar
            placeholder="Buscar en Negocios Online..."
            value={searchQuery}
            onChange={setSearchQuery}
            externalSections={searchShouldSearch ? searchSections : undefined}
            isLoading={searchIsLoading}
            onSelect={handleSearchSelect}
            showCommandPalette={true}
            onOpenCommandPalette={handleOpenCommandPalette}
          />
        </div>
       
        {/* Crear nuevo - solo ícono */}
        <div className={`${filaSecundaria} order-7 items-center`}>
          <CrearMenu
            onCrearDocumento={handleCrearDocumento}
            onCrearCliente={handleCrearCliente}
            onCrearProducto={handleCrearProducto}
          />
        </div>
      </div>

      {/* Right Section */}
      <div className="contents lg:flex lg:items-center">
        {/* Caja Status - solo visible cuando showCaja es true y cajaData existe */}
        {showCaja && cajaData && (
          <>
            <div className="order-3 flex items-center lg:order-none">
              <CajaStatus
                data={cajaData}
                cajasDisponibles={cajasDisponibles}
                cajaSeleccionadaId={cajaSeleccionadaId}
                onCambiarCaja={onCambiarCaja}
                onVerMovimientos={handleVerMovimientos}
                onCerrarCaja={handleCerrarCaja}
                onAbrirCaja={onAbrirCaja}
              />
            </div>
            <div className="hidden lg:block w-8"></div>
          </>
        )}

        {/* Más opciones (solo móvil y tablet) */}
        <Button
          iconOnly
          variant="ghost"
          size="md"
          onClick={() => setMasAbierto(abierto => !abierto)}
          active={masAbierto}
          aria-label="Más opciones"
          aria-expanded={masAbierto}
          icon={<MoreHorizontal className="w-5 h-5" />}
          className="order-4 lg:hidden"
        />

        {/* Salto a la fila secundaria (solo móvil y tablet) */}
        <div className={`${masAbierto ? 'block' : 'hidden'} order-6 w-full h-0 lg:hidden`} aria-hidden="true"></div>

        {/* Selector de Empresa */}
        {empresaActualId && sedeActualId && (
          <div className={`${filaSecundaria} order-8 items-center`}>
            <EmpresaSelector
              actual={{
                empresa: empresas.find(e => e.id === empresaActualId) || empresas[0],
                sede: sedes.find(s => s.id === sedeActualId) || sedes[0]
              }}
              empresas={empresas}
              sedes={sedes}
              onChangeWorkspace={handleChangeWorkspace}
              onAdministrarEmpresas={onAdministrarEmpresas}
            />
          </div>
        )}

        {/* Spacer 32px */}
        <div className="hidden lg:block w-8"></div>

        {/* Actions Group */}
        <div className={`${filaSecundaria} order-9 items-center gap-2`}>
          {/* Feedback */}
          <Tooltip text="Enviar retroalimentación" position="bottom">
            <Button
              iconOnly
              variant="ghost"
              size="md"
              aria-label="Enviar retroalimentación"
              onClick={onFeedback}
              icon={<MessageSquare className="w-[18px] h-[18px]" />}
            />
          </Tooltip>

          {/* Ayuda guiada */}
          <AyudaGuiadaMenu onCambiar={onAyudaGuiadaCambiar} tourModulo={tourModulo} tourProgreso={tourProgreso} />

          {/* Settings */}
          <Tooltip text="Configuración" position="bottom">
            <Button
              iconOnly
              variant="ghost"
              size="md"
              aria-label="Configuración"
              onClick={onSettings}
              active={settingsActive}
              icon={<Settings className="w-[18px] h-[18px]" />}
            />
          </Tooltip>

          {/* Notificaciones */}
          {notificacionesSlot}
        </div>
 
        {/* Spacer 24px */}
        <div className="hidden lg:block w-6"></div>

        {/* User Menu */}
        <div className="order-5 flex items-center lg:order-none">
          <UserMenu
            user={user}
            planStatus={planStatus}
            facturacionElectronicaEstado={facturacionElectronicaEstado}
            onElectronicInvoicingClick={onElectronicInvoicingClick}
            onProfileClick={onProfileClick}
            onHelpClick={onHelpClick}
            onSubscriptionsClick={onSubscriptionsClick}
            onShowKeyboardShortcuts={onShowKeyboardShortcuts}
            onLogoutClick={onLogout}
          />
        </div>
      </div>
    </header>
  );
};
